import { User } from "@ecologi/shared";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { parseQueryObject } from "../../lib";
import { useStore } from "../../store";

export function usePrivatePageGuard(redirectPathOnFailure?: string) {
  const { user, isCheckingUserSession } = useStore();
  const router = useRouter();
  const { usernameOrId, childUsernameOrId } = parseQueryObject(router.query);

  const isMatchingUsername = (
    user: User,
    rootUsername: string,
    childUsername: string
  ) => {
    if (rootUsername && childUsername) {
      return user.userId === childUsername || user.username === childUsername;
    } else if (rootUsername) {
      return user.userId === rootUsername || user.username === rootUsername;
    }

    return true;
  };

  const isUserAllowedAccess =
    user && isMatchingUsername(user, usernameOrId, childUsernameOrId);

  useEffect(() => {
    if (isCheckingUserSession) return;

    if (user) {
      if (!isUserAllowedAccess) {
        toast.error(`Not authorized to access ${window.location.pathname}`);
        router.replace(`/${user.username}`);
      }
    } else {
      if (redirectPathOnFailure) {
        router.replace(redirectPathOnFailure);
      } else {
        const returnTo = `${window.location.pathname}${window.location.search}`;
        window.location.href = `/auth/login?returnTo=${encodeURIComponent(
          returnTo
        )}`;
      }
    }
  }, [
    isCheckingUserSession,
    isUserAllowedAccess,
    redirectPathOnFailure,
    router,
    user,
  ]);

  return {
    isUserAllowedAccess,
    isLoading: !user || isCheckingUserSession,
  };
}
